import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useDeliveryQueueMutations = ({
  dealId,
  onSuccess,
  onError,
}: {
  dealId: Deal['id'];
  onSuccess?: (
    data: Deal,
    variables: { date_added: Deal['date_added_to_customer_delivery_queue'] },
  ) => void;
  onError?: (
    error: Error,
    variables: { date_added: Deal['date_added_to_customer_delivery_queue'] },
  ) => void;
}) => {
  const queryClient = useQueryClient();

  const enqueueDealMutation = useMutation({
    onMutate: async ({ date_added }) => {
      dealStore.addToCustomerDeliveryQueue({
        id: dealId,
        dateAdded: date_added,
      });
    },
    mutationFn: async ({
      date_added,
    }: {
      date_added: Deal['date_added_to_customer_delivery_queue'];
    }) => {
      const response = await pdkAxios.post(
        `/deals/${dealId}/enqueue-for-customer-delivery`,
        {
          date_added_to_customer_delivery_queue: date_added,
        },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating delivery queue', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const dequeueDealMutation = useMutation({
    onMutate: async () => {
      dealStore.removeFromCustomerDeliveryQueue({
        id: dealId,
      });
    },
    mutationFn: async () => {
      const response = await pdkAxios.post(
        `/deals/${dealId}/dequeue-from-customer-delivery`,
      );
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  return { enqueueDealMutation, dequeueDealMutation };
};

export default useDeliveryQueueMutations;
